import { useEffect, type RefObject } from 'react';
import { getGsap, hasFinePointer, prefersReducedMotion } from '@/lib/motion';

// useTilt — leans a card toward the pointer in 3D and lifts it a little
// (immersive landing, 2026-09-24). Mouse/trackpad only: a finger has no
// hover, so phones and tablets never get it, and reduced-motion visitors keep
// a flat card.
//
// Besides the rotation it publishes the pointer to CSS, so the card's own
// styles can draw the light that follows it:
//   --mx / --my  pointer position in px inside the card (spotlight, glare)
//   --px / --py  the same as -1..1 from the centre (media parallax)
// and sets data-tilt="on" while it is active, so that CSS only applies where
// the hook is really running.
//
// The pointer is measured against the element's PARENT: the card's own box
// changes as it rotates, and measuring a moving target makes it wobble.
//
// A GPU layer only while hovered (round 5, 2026-09-25). The 3D perspective
// used to be set on every card at mount, which made each card its own
// compositor layer for the life of the page: five big layers whose first
// raster all landed in the scroll that brought the bento in. Now a card
// takes its perspective (and will-change) when the pointer enters, and
// drops both once it has eased back flat after the pointer leaves.
//
// Not while the page scrolls under a resting mouse (round 5b, 2026-09-25).
// Inside a [data-scrolling] ancestor (useScrollCalm), a card that slides
// under the pointer does not lean or lift: that is the layer and the shader
// builds the scroll gate keeps out of the scroll. Only the pointer's own
// movement engages it then (the gate lifts on that movement too), and the
// first real move lifts a card whose entrance was skipped.

export interface TiltOptions {
  /** Largest lean each way, in degrees. The brief caps it at 6. */
  max?: number;
  /** How far the card rises while hovered, in px. */
  lift?: number;
  perspective?: number;
}

/** The return tween's length (the longest of the three below), plus a beat. */
const SETTLE_S = 0.85;

export function useTilt<T extends HTMLElement>(
  ref: RefObject<T | null>,
  { max = 5, lift = 6, perspective = 1100 }: TiltOptions = {},
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasFinePointer()) return undefined;
    const still = prefersReducedMotion();
    const { gsap } = getGsap();
    const frame = el.parentElement ?? el;

    const rotX = still ? null : gsap.quickTo(el, 'rotationX', { duration: 0.8, ease: 'power3.out' });
    const rotY = still ? null : gsap.quickTo(el, 'rotationY', { duration: 0.8, ease: 'power3.out' });
    const rise = still ? null : gsap.quickTo(el, 'y', { duration: 0.6, ease: 'power3.out' });
    el.dataset.tilt = 'on';

    let raised = false;
    let lifted = false;
    let flatten: ReturnType<typeof gsap.delayedCall> | null = null;
    const raise = () => {
      flatten?.kill();
      flatten = null;
      if (!lifted) {
        lifted = true;
        rise?.(-lift);
      }
      if (still || raised) return;
      raised = true;
      el.style.willChange = 'transform';
      gsap.set(el, { transformPerspective: perspective });
    };
    const scrolling = () => !!el.closest('[data-scrolling]');

    const onEnter = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || scrolling()) return;
      raise();
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (!(e.movementX || e.movementY) && scrolling()) return;
      const r = frame.getBoundingClientRect();
      if (!r.width || !r.height) return;
      raise();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const nx = Math.max(-1, Math.min(1, (x / r.width) * 2 - 1));
      const ny = Math.max(-1, Math.min(1, (y / r.height) * 2 - 1));
      el.style.setProperty('--mx', `${x.toFixed(1)}px`);
      el.style.setProperty('--my', `${y.toFixed(1)}px`);
      el.style.setProperty('--px', nx.toFixed(3));
      el.style.setProperty('--py', ny.toFixed(3));
      // Wide cards lean less around the vertical axis for the same pointer
      // travel, or the lead card's far edge swings out like a door.
      const yawScale = Math.min(1, 420 / r.width);
      rotY?.(nx * max * Math.max(0.55, yawScale));
      rotX?.(-ny * max);
    };
    const onLeave = () => {
      lifted = false;
      rotX?.(0);
      rotY?.(0);
      rise?.(0);
      el.style.setProperty('--px', '0');
      el.style.setProperty('--py', '0');
      if (!raised) return;
      flatten?.kill();
      // Flat again: back into the page's own layer.
      flatten = gsap.delayedCall(SETTLE_S, () => {
        flatten = null;
        raised = false;
        gsap.set(el, { clearProps: 'transform' });
        el.style.willChange = '';
      });
    };

    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      flatten?.kill();
      delete el.dataset.tilt;
      el.style.willChange = '';
      if (!still) {
        gsap.killTweensOf(el);
        gsap.set(el, { clearProps: 'transform' });
      }
    };
  }, [ref, max, lift, perspective]);
}
